import React, { FC, useEffect } from "react";

import { BaseComponentProps } from "@keepeek/refront-components";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import clsx from "clsx";

import {
  SpacerWidget as SpacerWidgetModel,
  SpacerWidgetHeight,
} from "../../../models/configuration/components/spacerWidget";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import { KWidgetProps } from "../models";

export type SpacerWidgetProps = {
  widgetId: string;
} & Omit<SpacerWidgetModel, "id"> &
  BaseComponentProps &
  KWidgetProps;

// Spacer wigget can add space between 2 widgets
const SpacerWidget: FC<React.PropsWithChildren<SpacerWidgetProps>> = function ({
  spacerWidgetHeight,
  testId = "SpacerWidget",
  id,
  className,
  widgetId,
  type,
}) {
  const { setWidgetState } = useWidgetState({
    id: widgetId,
    type,
  });
  const { breakpoints } = useTheme();
  // I assume that if it does not match any other it is XS by default
  const isSm = useMediaQuery(breakpoints.only("sm"));
  const isMd = useMediaQuery(breakpoints.only("md"));
  const isLg = useMediaQuery(breakpoints.only("lg"));
  const isXl = useMediaQuery(breakpoints.only("xl"));
  let currentMediaQuery: keyof SpacerWidgetHeight = "XS";
  if (isSm) {
    currentMediaQuery = "SM";
  }
  if (isMd) {
    currentMediaQuery = "MD";
  }
  if (isLg) {
    currentMediaQuery = "LG";
  }
  if (isXl) {
    currentMediaQuery = "XL";
  }

  // Create breakpoint interpretation order
  const breakpointsOrder: Array<keyof SpacerWidgetHeight> = ["XS", "SM", "MD", "LG", "XL"];
  // Calc the queries left before the currentMediaQuery
  const mediaQueriesHeight = breakpointsOrder.filter(
    (_bpo, index) => index < breakpointsOrder.indexOf(currentMediaQuery),
  );
  // Calc the height with the current media query or take the one just before
  let height: string | undefined = spacerWidgetHeight?.[currentMediaQuery];
  if (!height && !!spacerWidgetHeight) {
    mediaQueriesHeight.reverse().forEach((mq) => {
      if (!height && spacerWidgetHeight?.[mq]) {
        height = spacerWidgetHeight[mq];
      }
    });
  }

  useEffect(() => {
    setWidgetState({ state: WidgetUIState.RENDERED });
  }, [setWidgetState]);

  return (
    <Box
      data-testid={testId}
      style={{ height, flex: 1 }}
      className={clsx("SpacerWidget", `SpacerWidget-${widgetId}`, className)}
      id={id}
    />
  );
};

export default SpacerWidget;
